import React, { useContext } from 'react';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostFirewallPoliciesContext from '@totem/components/ubiquiti/hosts/details/firewallPolicies/ubiquitiHostFirewallPoliciesContext';
import UbiquitiHostFirewallZonesContext from '@totem/components/ubiquiti/hosts/details/firewallZones/ubiquitiHostFirewallZonesContext';
import {
  UbiquitiApiDataItem,
  UbiquitiFirewallPolicy,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const actionColor = (actionType: string) => {
  switch (actionType) {
    case 'ALLOW':
      return 'green';
    case 'BLOCK':
      return 'volcano';
    case 'REJECT':
      return 'orange';
    default:
      return 'default';
  }
};

const UbiquitiHostFirewallPoliciesTable = () => {
  const { loading, error, firewallPolicies } = useContext(
    UbiquitiHostFirewallPoliciesContext,
  );
  const { firewallZones } = useContext(UbiquitiHostFirewallZonesContext);

  const getZoneName = (zoneId: string): string => {
    const foundZone = firewallZones.find((chk) => chk.data.id === zoneId);
    return isNotNull(foundZone) ? foundZone.data.name : '<name unknown>';
  };

  const getZoneFilters = (zoneIds: string[]) =>
    Array.from(new Set(zoneIds))
      .map((zoneId) => ({ text: getZoneName(zoneId), value: zoneId }))
      .sort((compA, compB) => sortStringAscending(compA.text, compB.text));

  const sourceZoneFilters = getZoneFilters(
    firewallPolicies.map((policy) => policy.data.source.zoneId),
  );
  const destinationZoneFilters = getZoneFilters(
    firewallPolicies.map((policy) => policy.data.destination.zoneId),
  );

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiFirewallPolicy>>[] = [
    {
      title: 'Name',
      dataIndex: ['data', 'name'],
      key: 'name',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.name, compB.data.name),
    },
    {
      title: 'Enabled',
      dataIndex: ['data', 'enabled'],
      key: 'enabled',
      sorter: (compA, compB) =>
        Number(compA.data.enabled) - Number(compB.data.enabled),
      filters: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      onFilter: (value, record) => record.data.enabled === value,
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) =>
        record.data.enabled ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
    {
      title: 'Action',
      dataIndex: ['data', 'action', 'type'],
      key: 'action',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.action.type, compB.data.action.type),
      filters: [
        { text: 'Allow', value: 'ALLOW' },
        { text: 'Block', value: 'BLOCK' },
        { text: 'Reject', value: 'REJECT' },
      ],
      onFilter: (value, record) => record.data.action.type === value,
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) => (
        <Tag color={actionColor(record.data.action.type)}>
          {record.data.action.type}
        </Tag>
      ),
    },
    {
      title: 'Source Zone',
      dataIndex: ['data', 'source', 'zoneId'],
      key: 'sourceZone',
      sorter: (compA, compB) =>
        sortStringAscending(
          getZoneName(compA.data.source.zoneId),
          getZoneName(compB.data.source.zoneId),
        ),
      filters: sourceZoneFilters,
      onFilter: (value, record) => record.data.source.zoneId === value,
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) => (
        <Tag>{getZoneName(record.data.source.zoneId)}</Tag>
      ),
    },
    {
      title: 'Destination Zone',
      dataIndex: ['data', 'destination', 'zoneId'],
      key: 'destinationZone',
      sorter: (compA, compB) =>
        sortStringAscending(
          getZoneName(compA.data.destination.zoneId),
          getZoneName(compB.data.destination.zoneId),
        ),
      filters: destinationZoneFilters,
      onFilter: (value, record) => record.data.destination.zoneId === value,
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) => (
        <Tag>{getZoneName(record.data.destination.zoneId)}</Tag>
      ),
    },
    {
      title: 'IP Version',
      dataIndex: ['data', 'ipProtocolScope', 'ipVersion'],
      key: 'ipVersion',
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) => (
        <Tag>{record.data.ipProtocolScope.ipVersion}</Tag>
      ),
    },
    {
      title: 'Logging',
      dataIndex: ['data', 'loggingEnabled'],
      key: 'loggingEnabled',
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) =>
        record.data.loggingEnabled ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
    {
      title: 'Description',
      dataIndex: ['data', 'description'],
      key: 'description',
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallPolicy>) =>
        record.data.description,
    },
  ];

  const exportHeaders = [
    'Name',
    'Enabled',
    'Action',
    'Source Zone',
    'Destination Zone',
    'IP Version',
    'Logging',
    'Description',
  ];
  const exportRows = firewallPolicies.map((policy) => [
    policy.data.name,
    policy.data.enabled ? 'Yes' : 'No',
    policy.data.action.type,
    getZoneName(policy.data.source.zoneId),
    getZoneName(policy.data.destination.zoneId),
    policy.data.ipProtocolScope.ipVersion,
    policy.data.loggingEnabled ? 'Yes' : 'No',
    policy.data.description ?? '',
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Firewall policies unavailable"
        description="No firewall policy data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiFirewallPolicy>>
      showSorterTooltip
      columns={columns}
      dataSource={firewallPolicies}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="firewall-policies"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default UbiquitiHostFirewallPoliciesTable;
